import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaRegEdit, FaFileExcel } from "react-icons/fa";
import { toast } from 'react-toastify';
import api from "../../utils/api";
import { useUser } from '../../context/UserContext';
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import { MdAccountBalance } from 'react-icons/md';

function LeaveBalanceManagement() {
  const { currentUser, loading } = useUser();
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const cardsPerPage = 20;

  const [editForm, setEditForm] = useState({
    earnedLeaves: 0,
    sickLeaves: 0,
    compOffs: 0
  });

  const fetchLeaveBalances = async (page = 0, search = appliedSearchTerm) => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/leave-balance/all', {
        params: {
          page: page,
          size: cardsPerPage,
          sortBy: 'id',
          direction: 'asc',
          search: search
        }
      });
      console.log('Fetched leave balances:', response.data.content);
      setLeaveBalances(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      toast.error('Failed to fetch leave balances');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const response = await api.get('/api/leave-balance/export', {
        params: { search: appliedSearchTerm },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leave-balances-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel sheet downloaded successfully');
    } catch (error) {
      console.error('Error exporting leave balances:', error);
      toast.error('Failed to export leave balances');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchLeaveBalances(currentPage - 1, appliedSearchTerm);
    }
  }, [currentPage, appliedSearchTerm, loading]);

  const handleCardClick = (balance) => {
    setSelectedBalance(balance);
    setEditForm({
      earnedLeaves: balance.earnedLeaves || 0,
      sickLeaves: balance.sickLeaves || 0,
      compOffs: balance.compOffs || 0
    });
    setShowEditModal(true);
  };

  const handleUpdateBalance = async () => {
    if (!editForm.earnedLeaves && editForm.earnedLeaves !== 0) return;
    if (!editForm.sickLeaves && editForm.sickLeaves !== 0) return;
    if (!editForm.compOffs && editForm.compOffs !== 0) return;
    
    setIsSubmitting(true);
    try {
      await api.put(`/api/leave-balance/${selectedBalance.userId}`, editForm);
      toast.success('Leave balance updated successfully');
      fetchLeaveBalances(currentPage - 1);
      setShowEditModal(false);
      setSelectedBalance(null);
    } catch (error) {
      console.error('Error updating leave balance:', error);
      toast.error('Failed to update leave balance');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Backend search is now implemented, so we don't need frontend filtering.
  const filteredBalances = leaveBalances;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto">
      <motion.div 
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 bg-slate-200 rounded-2xl p-6 border border-slate-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex items-center gap-4 text-xl xs:text-2xl font-bold truncate">
          <div className="p-2 rounded-lg bg-slate-700 text-slate-100">
            <MdAccountBalance />
          </div>
          <h2 className="text-xl xs:text-2xl font-bold">Leave Balance Management</h2>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search by name or ID... (Press Enter)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedSearchTerm(searchTerm);
                  setCurrentPage(1);
                }
              }}
              className="w-full px-4 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          
          <button
            onClick={handleExportExcel}
            disabled={isExporting || isLoading}
            className="flex items-center justify-center gap-2 px-6 py-2 bg-green-600 text-white rounded-full 
                       hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors
                       font-medium whitespace-nowrap"
          >
            {isExporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FaFileExcel className="text-lg" />
                <span>Export Excel</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <svg
            className="animate-spin h-12 w-12 text-slate-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            ></path>
          </svg>
        </div>
      ) : filteredBalances.length > 0 ? (
        <>
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filteredBalances.map((balance, index) => (
              <motion.div
                key={balance.userId || index}
                className="bg-white rounded-2xl shadow-md border border-slate-100 p-5 cursor-pointer"
                onClick={() => handleCardClick(balance)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-800 text-lg truncate">
                      {balance.userName || 'Unknown Employee'}
                    </h3>
                  </div>
                  
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Earned Leave</p>
                      <p className="text-sm text-slate-500">EL</p>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                      {balance.earnedLeaves ?? 0}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="text-xs text-green-600 font-medium">Sick Leave</p>
                      <p className="text-sm text-slate-500">SL</p>
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                      {balance.sickLeaves ?? 0}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Compensatory Off</p>
                      <p className="text-sm text-slate-500">COMP OFF</p>
                    </div>
                    <div className="text-2xl font-bold text-purple-700">
                      {balance.compOffs ?? 0}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="flex justify-center mt-6">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <p className="text-slate-400 text-lg">
            {searchTerm ? 'No matching results found' : 'No leave balance records found'}
          </p>
        </div>
      )}

      {showEditModal && selectedBalance && (
        <div 
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (!isSubmitting && e.target === e.currentTarget) {
              setShowEditModal(false);
              setSelectedBalance(null);
            }
          }}
        >
          <motion.div 
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-800">Update Leave Balance</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedBalance.userName || 'Unknown Employee'}
                </p>
              </div>
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowEditModal(false);
                    setSelectedBalance(null);
                  }
                }}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 ${
                  isSubmitting ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Earned Leave (EL)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editForm.earnedLeaves}
                  onChange={(e) => setEditForm({...editForm, earnedLeaves: parseFloat(e.target.value) || 0})}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sick Leave (SL)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editForm.sickLeaves}
                  onChange={(e) => setEditForm({...editForm, sickLeaves: parseFloat(e.target.value) || 0})}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Compensatory Off (COMP OFF)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editForm.compOffs}
                  onChange={(e) => setEditForm({...editForm, compOffs: parseFloat(e.target.value) || 0})}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-600">Total Balance</span>
                  <span className="text-2xl font-bold text-slate-800">
                    {editForm.earnedLeaves + editForm.sickLeaves + editForm.compOffs}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBalance(null);
                  }}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateBalance}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-sm font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700 
                            disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <span>Update Balance</span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default LeaveBalanceManagement;